import React, { useState, useEffect, useMemo } from 'react';
import { format, addDays, parseISO, isSameDay } from 'date-fns';
import { TenantDetail, Service, fetchAvailableSlots } from '../api';
import { formatSalon, salonNow } from '../lib/datetime';

interface BookingCalendarProps {
  /** Needs the detail payload: `unavailableDates` drives the greyed-out days. */
  tenant: TenantDetail;
  service: Service;
  /** A specific worker id, or 'any' (the default) for the union of all qualified workers. */
  staffId?: number | 'any';
  onSelectTime: (time: Date) => void;
}

export default function BookingCalendar({
  tenant,
  service,
  staffId = 'any',
  onSelectTime,
}: BookingCalendarProps) {
  // Next 14 days, anchored to the salon's "today". Keyed on the date so a page
  // left open overnight rolls onto the new day instead of showing a stale strip.
  const todayKey = format(salonNow(), 'yyyy-MM-dd');
  const availableDates = useMemo(() => {
    const start = parseISO(todayKey);
    return Array.from({ length: 14 }, (_, i) => addDays(start, i));
  }, [todayKey]);

  const isDayClosed = (date: Date) =>
    !tenant.businessHours[format(date, 'EEEE')] ||
    tenant.unavailableDates.includes(format(date, 'yyyy-MM-dd'));

  /**
   * True once today's last booking has come and gone.
   *
   * `unavailableDates` is about the salon's capacity, not the clock, so today
   * stays "open" there right up to midnight — but every slot is already in the
   * past, so opening on it shows an empty list. Only used to pick the initial
   * day: today still renders normally and stays clickable, because "no times
   * left today" is a true and useful thing for a customer to be able to check.
   */
  const todayIsSpent = () => {
    const closesAt = tenant.businessHours[format(salonNow(), 'EEEE')]?.close;
    if (!closesAt) return false;
    const [h, m] = closesAt.split(':').map(Number);
    const now = salonNow();
    return now.getHours() * 60 + now.getMinutes() >= h * 60 + m;
  };

  // Open on the first day that can actually be booked. Landing on a greyed-out
  // today and reading "No available times" looks like the salon is broken.
  const [selectedDate, setSelectedDate] = useState<Date>(
    () =>
      availableDates.find((d, i) => !isDayClosed(d) && !(i === 0 && todayIsSpent())) ??
      availableDates[0],
  );
  const [slots, setSlots] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Guarded against out-of-order responses: closed days now return [] almost
    // instantly while open ones do real work, so clicking through the strip can
    // easily land a stale response and paint slots on the wrong day.
    let cancelled = false;
    setLoading(true);
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    fetchAvailableSlots(tenant.slug, dateStr, service.id, staffId)
      .then((s) => {
        if (!cancelled) setSlots(s);
      })
      .catch(() => {
        if (!cancelled) setSlots([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedDate, tenant.slug, service.id, staffId]);

  return (
    <div className="border-default bg-surface mt-8 rounded-2xl border p-6 shadow-sm">
      <h3 className="text-fg mb-4 text-lg font-semibold">Select a Date & Time</h3>

      {/* Date Selector */}
      <div className="hide-scrollbar flex snap-x space-x-3 overflow-x-auto pb-4">
        {availableDates.map((date) => {
          const isSelected = isSameDay(date, selectedDate);
          const dayName = format(date, 'EEE');
          const dayNumber = format(date, 'd');
          // A day the salon simply doesn't open reads "Closed"; one lost to time
          // off reads "Away", so a normal Sunday isn't confused with a holiday.
          const shutThisWeekday = !tenant.businessHours[format(date, 'EEEE')];
          const away =
            !shutThisWeekday && tenant.unavailableDates.includes(format(date, 'yyyy-MM-dd'));
          const isClosed = shutThisWeekday || away;

          return (
            <button
              key={date.toISOString()}
              onClick={() => !isClosed && setSelectedDate(date)}
              disabled={isClosed}
              title={away ? 'Away — no bookings this day' : shutThisWeekday ? 'Closed' : undefined}
              className={`flex h-24 w-20 flex-shrink-0 snap-start flex-col items-center justify-center rounded-xl border transition-colors ${
                isSelected
                  ? 'border-brand-600 bg-brand-600 text-white'
                  : away
                    ? 'border-warn/30 bg-warn-soft text-warn cursor-not-allowed'
                    : shutThisWeekday
                      ? 'border-subtle bg-canvas text-fg-subtle cursor-not-allowed'
                      : 'border-default bg-surface text-fg hover:border-strong'
              }`}
            >
              <span className="text-xs font-medium uppercase">{dayName}</span>
              <span className="mt-1 text-xl font-bold">{dayNumber}</span>
              {!isSelected && isClosed && (
                <span className="mt-0.5 text-[10px] font-medium">{away ? 'Away' : 'Closed'}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Time Slots */}
      <div className="mt-6">
        {loading ? (
          <div className="bg-canvas text-fg-muted rounded-xl py-8 text-center">
            Loading available times...
          </div>
        ) : slots.length > 0 ? (
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
            {slots.map((slot) => (
              <button
                key={slot}
                onClick={() => onSelectTime(new Date(slot))}
                className="hover:border-brand-500 hover:bg-accent-soft border-default text-fg rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
              >
                {formatSalon(slot, 'h:mm a')}
              </button>
            ))}
          </div>
        ) : (
          <div className="bg-canvas text-fg-muted rounded-xl py-8 text-center">
            No available times on this date.
          </div>
        )}
      </div>
    </div>
  );
}
